import { Injectable } from '@angular/core';
import {
    COLUMN_GAP,
    FONT_SIZE_BODY,
    HEADER_LOGO_WIDTH,
    LABEL_WIDTH,
    PAGE_BOTTOM_RESERVE
} from './nachricht-pdf.constants';

export interface LabelValueLayout {
    startX: number;
    startY: number;
    valueX: number;
    valueWidth: number;
}

@Injectable({
    providedIn: 'root'
})
export class NachrichtPdfUtilsService {

    public createSection(
        doc: any,
        parent: any,
        title: string,
        lang: string
    ): any {
        const section = doc.struct('Sect', { title, lang } as any);
        parent.add(section);
        return section;
    }

    public getUsablePageWidth(doc: any): number {
        return doc.page.width - doc.page.margins.left - doc.page.margins.right;
    }

    public getLabelValueLayout(doc: any): LabelValueLayout {
        const startX = doc.page.margins.left;
        const startY = doc.y;
        const valueX = startX + LABEL_WIDTH + COLUMN_GAP;
        const valueWidth = doc.page.width - doc.page.margins.right - valueX;

        return {
            startX,
            startY,
            valueX,
            valueWidth
        };
    }

    public measureTextHeight(
        doc: any,
        fontName: string,
        fontSize: number,
        text: string,
        width: number
    ): number {
        doc.font(fontName).fontSize(fontSize);
        return doc.heightOfString(text, { width });
    }

    public calculateRowHeight(doc: any, label: string, value: string): number {
        const valueWidth = this.getUsablePageWidth(doc) - LABEL_WIDTH - COLUMN_GAP;

        const labelHeight = this.measureTextHeight(doc, 'BodyFont-Bold', FONT_SIZE_BODY, label, LABEL_WIDTH);
        const valueHeight = this.measureTextHeight(doc, 'BodyFont', FONT_SIZE_BODY, value, valueWidth);

        return Math.max(labelHeight, valueHeight);
    }

    public ensurePageBreak(doc: any, neededHeight: number): void {
        const maxY = doc.page.height - doc.page.margins.bottom - PAGE_BOTTOM_RESERVE;

        if (doc.y + neededHeight > maxY) {
            doc.addPage();
        }
    }

    public drawArtifactImage(
        doc: any,
        imageBuffer: ArrayBuffer,
        x: number,
        y: number,
        width: number
    ): void {
        doc.markContent('Artifact', { type: 'Layout' } as any);
        doc.image(imageBuffer, x, y, { width });
        doc.endMarkedContent();
    }

    public getHeaderLogoX(doc: any): number {
        return doc.page.width - doc.page.margins.right - HEADER_LOGO_WIDTH;
    }

    public dataUriToArrayBuffer(dataUri: string): ArrayBuffer {
        if (!dataUri?.startsWith('data:image')) {
            throw new Error('Ungültige Data-URI für Bild.');
        }

        const [, base64Content] = dataUri.split(',');

        if (!base64Content) {
            throw new Error('Base64-Inhalt im Bild fehlt.');
        }

        const normalizedBase64 = base64Content.replaceAll(/\s/g, '');
        const binary = atob(normalizedBase64);
        const bytes = new Uint8Array(binary.length);

        for (let i = 0; i < binary.length; i++) {
            bytes[i] = binary.codePointAt(i);
        }

        return bytes.buffer.slice(0);
    }
}